import { useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { compensabileCent, motivoNonCompensabile, versoCompensazione } from '@mastro/core/compensazioni';
import { api, type Categoria, type Documento, type DocumentoCompensabile, type Movimento } from '../api';
import {
  Campo,
  Caricamento,
  Dialogo,
  Errore,
  Etichetta,
  Importo,
  Scheda,
  Vuoto,
  useAlberoCategorie,
  useAvvisi,
  useCarico,
  useDebounce,
} from '../componenti/base';
import { etichettaStato, formattaData, formattaEuro, numero, percentuale, oggi } from '../formato';
import { useSessione } from '../main';

export function DettaglioDocumento() {
  const { id } = useParams<{ id: string }>();
  const { organizzazione } = useSessione();
  const avvisa = useAvvisi();
  const vai = useNavigate();
  /**
   * L'elenco da cui si è arrivati, filtri compresi. Chi lavora quattro fatture
   * dello stesso cliente non deve rifare la ricerca quattro volte. Se si è
   * entrati da un'altra strada — un collegamento diretto, una compensazione —
   * si torna all'elenco intero, che è la risposta giusta quando un elenco di
   * partenza non c'è.
   */
  const daDove = (useLocation().state as { elenco?: string } | null)?.elenco;
  const [cercaMovimento, setCercaMovimento] = useState(false);
  const [correggiRate, setCorreggiRate] = useState(false);
  const [compensa, setCompensa] = useState(false);
  const { dati, caricamento, errore, ricarica } = useCarico(() => api.get<Documento>(`/api/documenti/${id}`), [id]);

  if (caricamento) return <Caricamento />;
  if (errore || !dati) return <div className="p-6"><Errore testo={errore ?? 'Documento non trovato'} riprova={ricarica} /></div>;

  const d = dati;
  const disabbina = async (movimentoId: string, dicitura: string) => {
    // Il riepilogo prima della conferma serve a far vedere *quale* dei
    // movimenti si sta disabbinando: su una fattura con più abbinamenti, un «sei
    // sicuro?» generico non permette di accorgersi di aver cliccato la riga
    // sbagliata.
    if (!confirm(`Disabbinare «${dicitura}» dalla fattura ${d.numero}?\n\nIl movimento resta in banca: sparisce solo il collegamento, e la fattura torna da incassare per quella quota.`))
      return;
    try {
      await api.del('/api/riconciliazione/abbina', { movimentoId, documentoId: d.id });
      avvisa('Abbinamento rimosso. Il movimento è tornato libero.');
      ricarica();
    } catch (e) {
      avvisa(e instanceof Error ? e.message : 'Non è stato possibile disabbinare il movimento.', 'errore');
    }
  };

  /**
   * Storno di una compensazione. Si cancella la riga e i due residui risalgono
   * da soli: quello che resta è nel registro, che non si cancella. Tenerla
   * come «annullata» costringerebbe ogni somma a ricordarsi di escluderla.
   */
  const storna = async (compensazioneId: string, con: string, importoCent: number) => {
    const nota = prompt(
      `Stornare la compensazione di ${formattaEuro(importoCent)} con la fattura ${con}?\n\nLe due fatture tornano aperte per quella quota. Scrivi perché: resta a registro.`,
    );
    if (nota === null) return;
    if (nota.trim().length < 3) {
      avvisa('Serve una ragione di almeno tre caratteri: lo storno resta a registro.', 'errore');
      return;
    }
    try {
      await api.del(`/api/compensazioni/${compensazioneId}`, { nota: nota.trim() });
      avvisa('Compensazione stornata. Le due fatture sono tornate aperte.');
      ricarica();
    } catch (e) {
      avvisa(e instanceof Error ? e.message : 'Storno non riuscito.', 'errore');
    }
  };

  const elimina = async () => {
    if (!confirm(`Eliminare la fattura ${d.numero}? L'operazione non è reversibile.`)) return;
    try {
      await api.del(`/api/documenti/${d.id}`);
      avvisa(`Fattura ${d.numero} eliminata.`);
      vai(d.tipo === 'ATTIVA' ? '/fatture/attive' : '/fatture/passive');
    } catch (e) {
      avvisa(e instanceof Error ? e.message : 'Eliminazione non riuscita.', 'errore');
    }
  };

  return (
    <div className="pagina space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link
            to={daDove ?? (d.tipo === 'ATTIVA' ? '/fatture/attive' : '/fatture/passive')}
            className="text-xs text-inchiostro-500 underline underline-offset-2"
          >
            ← {d.tipo === 'ATTIVA' ? 'Fatture attive' : 'Fatture passive'}
          </Link>
          <h1 className="mt-1 flex items-center gap-2 text-lg font-semibold tracking-tight">
            Fattura {d.numero}
            <Etichetta
              testo={etichettaStato(d.statoEsteso, d.tipo)}
              tono={d.statoEsteso === 'PAGATA' ? 'ok' : d.statoEsteso === 'SCADUTA' ? 'allerta' : d.statoEsteso === 'PARZIALE' ? 'attenzione' : 'neutro'}
            />
            {d.notaDiCredito && <Etichetta testo="Nota di credito" tono="attenzione" />}
          </h1>
          <p className="text-sm text-inchiostro-500">{d.controparte?.denominazione ?? 'Controparte non indicata'}</p>
        </div>
        <div className="flex gap-2">
          {d.haXmlOriginale && (
            <a className="bottone-secondario" href={`/api/documenti/${d.id}/xml`}>
              Scarica XML originale
            </a>
          )}
          <button className="bottone-pericolo" onClick={elimina}>
            Elimina
          </button>
        </div>
      </header>

      <div className="grid gap-4 lg:grid-cols-3">
        <Scheda titolo="Dati del documento">
          <dl className="space-y-2 text-sm">
            <Voce t="Tipo documento SDI" v={d.tipoDocumentoSdi} />
            <Voce t="Competenza" v={formattaData(d.dataCompetenza, organizzazione.formatoData)} aiuto="Alimenta il conto economico" />
            <Voce
              t="Scadenza nominale"
              v={d.dataScadenzaNominale ? formattaData(d.dataScadenzaNominale, organizzazione.formatoData) : '—'}
            />
            <Voce
              t="Cassa attesa"
              v={formattaData(d.dataCassaAttesa, organizzazione.formatoData)}
              aiuto="Scadenza corretta col ritardo medio storico della controparte"
            />
            <Voce
              t="Cassa effettiva"
              v={d.dataCassaEffettiva ? formattaData(d.dataCassaEffettiva, organizzazione.formatoData) : '—'}
              aiuto="Valorizzata dalla riconciliazione, non scritta a mano"
            />
            <Voce t="Origine" v={d.origine === 'XML_SDI' ? 'XML FatturaPA' : d.origine === 'FATTURA_IN_CLOUD' ? 'Fatture in Cloud' : 'Inserimento manuale'} />
            <Voce
              t="Voce del previsionale"
              v={<CampoVoce documento={d} onCambiata={ricarica} />}
              aiuto="Sotto quale voce pesa nel previsionale. «Come l’anagrafica» usa quella della controparte."
            />
            {d.causale && <Voce t="Causale" v={d.causale} />}
          </dl>
        </Scheda>

        <Scheda titolo="Importi">
          <dl className="space-y-2 text-sm">
            <Voce t="Imponibile" v={<Importo cent={d.imponibileCent} />} />
            <Voce t="IVA" v={<Importo cent={d.ivaCent} />} />
            <Voce t="Totale" v={<Importo cent={d.totaleCent} grassetto />} />
            {d.ritenutaCent > 0 && (
              <>
                <Voce
                  t="Ritenuta d’acconto"
                  v={<Importo cent={-d.ritenutaCent} />}
                  aiuto="La trattiene il cliente e la versa lui all'erario: non passerà mai dalla banca."
                />
                <Voce
                  t="Atteso in banca"
                  v={<Importo cent={d.totaleCent - d.ritenutaCent} grassetto />}
                  aiuto="È su questo che si chiude la riconciliazione, non sul totale."
                />
              </>
            )}
            <Voce t="Già riconciliato" v={<Importo cent={d.allocatoCent} tono="incassi" />} />
            <Voce
              t="Residuo"
              v={<Importo cent={d.residuoCent} tono={d.tipo === 'ATTIVA' ? 'incassi' : 'uscite'} grassetto />}
              aiuto="È questo che pesa sul previsionale"
            />
          </dl>
          {d.statoOrigineEsterna === 'PAGATA' && d.stato !== 'PAGATA' && (
            <p className="mt-3 rounded border border-saldo/30 bg-saldo-chiaro p-2 text-xs text-saldo">
              Fatture in Cloud dà questa fattura per pagata
              {d.dataPagamentoEsterna ? ` il ${formattaData(d.dataPagamentoEsterna, organizzazione.formatoData)}` : ''}, ma
              non c’è ancora un movimento bancario abbinato. Le due cose sono tenute separate: nessun movimento viene
              creato per far tornare i conti.
            </p>
          )}
        </Scheda>

        <Scheda titolo="Controparte">
          {d.controparte ? (
            <dl className="space-y-2 text-sm">
              <Voce t="Denominazione" v={d.controparte.denominazione} />
              <Voce t="Partita IVA" v={d.controparte.partitaIva ?? '—'} />
              <Link to="/anagrafiche" className="mt-2 inline-block text-xs underline underline-offset-2">
                Apri anagrafiche
              </Link>
            </dl>
          ) : (
            <p className="text-sm text-inchiostro-500">Nessuna controparte collegata.</p>
          )}
        </Scheda>
      </div>

      <Scheda titolo="Righe" senzaPadding>
        <div className="overflow-x-auto">
          <table className="dati">
            <thead>
              <tr>
                <th className="w-12">#</th>
                <th>Descrizione</th>
                <th className="text-right">Qtà</th>
                <th>U.M.</th>
                <th className="text-right">Prezzo unit.</th>
                <th className="text-right">Sconto</th>
                <th className="text-right">IVA</th>
                <th className="text-right">Totale</th>
              </tr>
            </thead>
            <tbody>
              {(d.righe ?? []).map((r) => (
                <tr key={r.numeroLinea}>
                  <td className="num text-inchiostro-500">{r.numeroLinea}</td>
                  <td>{r.descrizione}</td>
                  <td className="num text-right">{numero(r.quantita, 3)}</td>
                  <td>{r.unitaMisura ?? '—'}</td>
                  <td className="text-right"><Importo cent={r.prezzoUnitarioCent} /></td>
                  <td className="num text-right">{r.scontoPercentuale ? percentuale(r.scontoPercentuale) : '—'}</td>
                  <td className="num text-right">
                    {percentuale(r.aliquotaIva)}
                    {r.natura && <span className="ml-1 text-xs text-inchiostro-500">{r.natura}</span>}
                  </td>
                  <td className="text-right"><Importo cent={r.prezzoTotaleCent} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Scheda>

      <div className="grid gap-4 lg:grid-cols-2">
        <Scheda titolo="Riepilogo IVA" senzaPadding>
          <table className="dati">
            <thead>
              <tr>
                <th>Aliquota</th>
                <th>Natura</th>
                <th className="text-right">Imponibile</th>
                <th className="text-right">Imposta</th>
              </tr>
            </thead>
            <tbody>
              {(d.riepiloghiIva ?? []).map((r, i) => (
                <tr key={i}>
                  <td className="num">{percentuale(r.aliquotaIva)}</td>
                  <td>{r.natura ?? '—'}</td>
                  <td className="text-right"><Importo cent={r.imponibileCent} /></td>
                  <td className="text-right"><Importo cent={r.impostaCent} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Scheda>

        <Scheda
          titolo="Scadenze"
          sottotitolo="La data di cassa attesa tiene conto del ritardo storico"
          senzaPadding
          azioni={
            <button className="bottone-secondario px-2 py-1 text-xs" onClick={() => setCorreggiRate(true)}>
              Correggi
            </button>
          }
        >
          <table className="dati">
            <thead>
              <tr>
                <th>Scadenza</th>
                <th>Cassa attesa</th>
                <th className="text-right">Importo</th>
                <th>Stato</th>
              </tr>
            </thead>
            <tbody>
              {(d.scadenze ?? []).map((s) => (
                <tr key={s.id}>
                  <td className="num">{formattaData(s.dataScadenza, organizzazione.formatoData)}</td>
                  <td className="num">
                    {formattaData(s.dataCassaAttesa, organizzazione.formatoData)}
                    {s.dataCassaAttesa < oggi() && d.stato !== 'PAGATA' && (
                      <span className="ml-2"><Etichetta testo="scaduta" tono="allerta" /></span>
                    )}
                  </td>
                  <td className="text-right"><Importo cent={s.importoCent} /></td>
                  <td>
                    {s.statoOrigineEsterna === 'PAGATA' ? (
                      <Etichetta testo="pagata su FIC" tono="info" />
                    ) : (
                      <span className="text-xs text-inchiostro-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Scheda>
      </div>

      <Scheda
        titolo="Movimenti abbinati"
        sottotitolo="Un bonifico può coprire più fatture e una fattura può essere pagata da più movimenti"
        senzaPadding
        azioni={
          d.residuoCent > 0 ? (
            <button className="bottone-primario" onClick={() => setCercaMovimento(true)}>
              Cerca un movimento
            </button>
          ) : undefined
        }
      >
        {(d.abbinamenti ?? []).length === 0 ? (
          <div className="p-6 text-center text-sm text-inchiostro-500">
            Nessun movimento abbinato.{' '}
            <Link to="/riconciliazione" className="underline underline-offset-2">
              Vai alla riconciliazione
            </Link>
          </div>
        ) : (
          <table className="dati">
            <thead>
              <tr>
                <th>Data</th>
                <th>Conto</th>
                <th>Causale</th>
                <th className="text-right">Quota su questa fattura</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {d.abbinamenti!.map((a) => (
                <tr key={a.movimentoId}>
                  <td className="num">{formattaData(a.data, organizzazione.formatoData)}</td>
                  <td>{a.conto}</td>
                  <td className="max-w-96 truncate" title={a.dicitura}>{a.dicitura}</td>
                  <td className="text-right"><Importo cent={a.importoCent} tono="incassi" /></td>
                  <td className="text-right">
                    <button
                      className="bottone-secondario px-2 py-1 text-xs"
                      title="Toglie il collegamento fra questo movimento e la fattura. Non cancella né l'uno né l'altra."
                      onClick={() => disabbina(a.movimentoId, a.dicitura)}
                    >
                      Disabbina
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Scheda>

      {/* Una fattura può essere chiusa senza che in banca passi niente: è lo
          scambio merce, dove si emette fattura e se ne riceve una in
          compensazione. La scheda sta accanto a quella dei movimenti, non al
          suo posto: su un documento coperto in parte da bonifico e in parte da
          compensazione, vederne solo uno farebbe sembrare il residuo sbagliato. */}
      <Scheda
        titolo="Compensazioni"
        sottotitolo="Chiuse contro un documento di verso opposto della stessa controparte — fattura o nota di credito. In banca non passa niente."
        senzaPadding
        azioni={
          d.residuoCent > 0 && d.controparte ? (
            <button className="bottone-secondario" onClick={() => setCompensa(true)}>
              Compensa con un documento
            </button>
          ) : undefined
        }
      >
        {(d.compensazioni ?? []).length === 0 ? (
          <div className="p-6 text-center text-sm text-inchiostro-500">
            Nessuna compensazione su questa fattura.
          </div>
        ) : (
          <table className="dati">
            <thead>
              <tr>
                <th className="w-32">Data</th>
                <th className="w-40">Con la fattura</th>
                <th className="w-36 text-right">Importo</th>
                <th>In base a cosa</th>
                <th className="w-56" />
              </tr>
            </thead>
            <tbody>
              {d.compensazioni!.map((c) => (
                <tr key={c.id}>
                  <td className="num">{formattaData(c.data, organizzazione.formatoData)}</td>
                  <td>
                    {/* Si tramanda l'elenco di partenza, non questa pagina:
                        passando da una fattura all'altra il «torna indietro»
                        deve riportare da dove si era cominciato. */}
                    <Link
                      to={`/fatture/${c.controDocumentoId}`}
                      state={{ elenco: daDove }}
                      className="underline underline-offset-2"
                    >
                      {c.controDocumentoNumero}
                    </Link>
                  </td>
                  <td className="text-right"><Importo cent={c.importoCent} /></td>
                  <td className="text-sm">
                    {c.nota}
                    <span className="mt-0.5 block text-xs text-inchiostro-500">{c.utenteNome}</span>
                  </td>
                  <td className="text-right">
                    <a
                      className="bottone-secondario px-2 py-1 text-xs"
                      href={`/compensazioni/${c.id}/lettera`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Lettera
                    </a>{' '}
                    <button
                      className="bottone-secondario px-2 py-1 text-xs"
                      title="Le due fatture tornano aperte per questa quota. Non cancella né l’una né l’altra."
                      onClick={() => storna(c.id, c.controDocumentoNumero, c.importoCent)}
                    >
                      Storna
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Scheda>

      {/* Il registro va **letto**, non solo scritto: un archivio che nessuno può
          consultare non risponde alla domanda per cui esiste. Compare solo
          quando c'è qualcosa, così un documento intatto resta pulito. */}
      {(d.storia ?? []).length > 0 && (
        <Scheda
          titolo="Registro delle correzioni"
          sottotitolo="Cosa è stato cambiato a mano su questo documento, da chi e perché. In sola aggiunta."
          senzaPadding
        >
          <table className="dati">
            <thead>
              <tr>
                <th className="w-44">Quando</th>
                <th className="w-40">Chi</th>
                <th className="w-52">Cosa</th>
                <th>Perché</th>
              </tr>
            </thead>
            <tbody>
              {(d.storia ?? []).map((e) => (
                <tr key={e.id}>
                  <td className="num whitespace-nowrap text-xs">
                    {formattaData(e.creatoIl.slice(0, 10), organizzazione.formatoData)}{' '}
                    <span className="text-inchiostro-500">{e.creatoIl.slice(11, 16)}</span>
                  </td>
                  <td className="text-xs">{e.utenteNome}</td>
                  <td>
                    <Etichetta testo={etichettaAzione(e.azione)} />
                  </td>
                  <td className="text-sm">
                    {e.nota}
                    <DettaglioCorrezione dettaglio={e.dettaglio} formatoData={organizzazione.formatoData} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Scheda>
      )}

      {cercaMovimento && (
        <DialogoCercaMovimento
          documento={d}
          onChiudi={() => setCercaMovimento(false)}
          onAbbinato={() => {
            setCercaMovimento(false);
            ricarica();
          }}
        />
      )}
      {compensa && (
        <DialogoCompensa
          documento={d}
          onChiudi={() => setCompensa(false)}
          onFatto={() => {
            setCompensa(false);
            ricarica();
          }}
        />
      )}
      {correggiRate && (
        <DialogoScadenze
          documento={d}
          onChiudi={() => setCorreggiRate(false)}
          onFatto={() => {
            setCorreggiRate(false);
            ricarica();
          }}
        />
      )}
    </div>
  );
}

const AZIONI: Record<string, string> = {
  SCADENZE_CORRETTE: 'scadenze corrette',
  COMPENSATA: 'compensata',
  COMPENSAZIONE_STORNATA: 'compensazione stornata',
};
const etichettaAzione = (a: string) => AZIONI[a] ?? a.toLowerCase().replace(/_/g, ' ');

/**
 * Da cosa a cosa. Il registro senza il valore di partenza direbbe che qualcosa
 * è cambiato ma non **cosa**, e per ricostruirlo servirebbe una versione
 * precedente che non esiste da nessuna parte.
 */
function DettaglioCorrezione({
  dettaglio,
  formatoData,
}: {
  dettaglio: unknown;
  formatoData: Parameters<typeof formattaData>[1];
}) {
  const d = dettaglio as
    | {
        prima?: { dataScadenza: string; importoCent: number }[];
        dopo?: { dataScadenza: string; importoCent: number }[];
        con?: string;
        importoCent?: number;
        data?: string;
      }
    | null;

  // Una compensazione dice con quale fattura e per quanto: senza, la riga
  // direbbe che qualcosa è stato compensato ma non contro cosa, che è
  // esattamente l'informazione per cui la si legge.
  if (d?.con && typeof d.importoCent === 'number') {
    return (
      <span className="mt-0.5 block text-xs text-inchiostro-500">
        {formattaEuro(d.importoCent)} contro la fattura {d.con}
        {d.data ? ` · accordo del ${formattaData(d.data, formatoData)}` : ''}
      </span>
    );
  }

  const prima = d?.prima;
  if (!prima?.length || !d?.dopo?.length) return null;
  const riga = (r: { dataScadenza: string; importoCent: number }) =>
    `${formattaData(r.dataScadenza, formatoData)} · ${formattaEuro(r.importoCent)}`;
  return (
    <span className="mt-0.5 block text-xs text-inchiostro-500">
      da {prima.map(riga).join(' + ')} a {d.dopo.map(riga).join(' + ')}
    </span>
  );
}

/** Da euro digitati a centesimi interi, senza passare per i decimali. */
function inCent(testo: string): number {
  const pulito = testo.replace(/\s|€/g, '').replace(/\./g, '').replace(',', '.');
  if (!pulito) return 0;
  const n = Number(pulito);
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
}
const inEuro = (cent: number) => (cent === 0 ? '' : (cent / 100).toFixed(2).replace('.', ','));

/**
 * Correggere le rate di un documento.
 *
 * Serve perché **la fattura può arrivare sbagliata**: un XML con la scadenza
 * del 2016 su una fattura del 2026 fa trascinare quel debito in cima allo
 * scadenzario per sempre. Mastro legge fedelmente ciò che riceve, quindi la
 * correzione dev'essere un gesto esplicito di una persona — e resta visibile,
 * perché la data corretta smette di coincidere con quella del documento
 * originale.
 */
function DialogoScadenze({
  documento,
  onChiudi,
  onFatto,
}: {
  documento: Documento;
  onChiudi: () => void;
  onFatto: () => void;
}) {
  const { organizzazione } = useSessione();
  const avvisa = useAvvisi();
  const [righe, setRighe] = useState(
    (documento.scadenze ?? []).map((s) => ({
      id: s.id as string | undefined,
      dataScadenza: s.dataScadenza,
      importo: inEuro(s.importoCent),
      abbinata: Boolean(s.abbinata),
    })),
  );
  const [nota, setNota] = useState('');
  const [attesa, setAttesa] = useState(false);

  const somma = righe.reduce((t, r) => t + inCent(r.importo), 0);
  const scarto = somma - documento.totaleCent;
  const complete = righe.every((r) => r.dataScadenza.length === 10 && inCent(r.importo) > 0);
  const puoSalvare = righe.length > 0 && complete && scarto === 0 && nota.trim().length >= 3;

  const cambia = (i: number, patch: Partial<(typeof righe)[number]>) =>
    setRighe((v) => v.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  const salva = async () => {
    setAttesa(true);
    try {
      await api.put(`/api/documenti/${documento.id}/scadenze`, {
        nota: nota.trim(),
        scadenze: righe.map((r) => ({
          id: r.id,
          dataScadenza: r.dataScadenza,
          importoCent: inCent(r.importo),
        })),
      });
      avvisa('Scadenze corrette. La data di cassa attesa è stata ricalcolata col ritardo della controparte.');
      onFatto();
    } catch (e) {
      avvisa(e instanceof Error ? e.message : 'Correzione non riuscita.', 'errore');
    } finally {
      setAttesa(false);
    }
  };

  return (
    <Dialogo aperto titolo="Correggi le scadenze" onChiudi={onChiudi} larghezza="max-w-2xl">
      <div className="mb-3 rounded-md bg-inchiostro-50 p-3 text-sm">
        <p className="font-medium">
          {documento.numero} · {documento.controparte?.denominazione ?? 'senza controparte'}
        </p>
        <p className="mt-0.5 text-inchiostro-600">
          Documento del {formattaData(documento.dataCompetenza, organizzazione.formatoData)}, totale{' '}
          <span className="num font-medium">{formattaEuro(documento.totaleCent)}</span>
        </p>
      </div>

      <table className="dati">
        <thead>
          <tr>
            <th className="w-48">Scadenza</th>
            <th className="w-40 text-right">Importo</th>
            <th className="w-10" />
          </tr>
        </thead>
        <tbody>
          {righe.map((r, i) => (
            <tr key={r.id ?? `nuova-${i}`}>
              <td>
                <input
                  type="date"
                  className="campo"
                  aria-label={`Scadenza rata ${i + 1}`}
                  value={r.dataScadenza}
                  onChange={(e) => cambia(i, { dataScadenza: e.target.value })}
                />
              </td>
              <td>
                <input
                  className="campo num text-right"
                  aria-label={`Importo rata ${i + 1}`}
                  value={r.importo}
                  onChange={(e) => cambia(i, { importo: e.target.value })}
                  placeholder="0,00"
                />
              </td>
              <td>
                {/* Una rata già pagata non si toglie: il pagamento la indica, e
                    cancellarla lascerebbe il movimento attaccato al documento
                    senza più sapere quale rata copriva. */}
                {righe.length > 1 && !r.abbinata && (
                  <button
                    className="text-allerta"
                    aria-label={`Togli la rata ${i + 1}`}
                    onClick={() => setRighe((v) => v.filter((_, j) => j !== i))}
                  >
                    ×
                  </button>
                )}
                {r.abbinata && (
                  <Etichetta
                    testo="pagata"
                    tono="ok"
                    titolo="C’è un movimento collegato a questa rata: per toglierla, stacca prima l’abbinamento."
                  />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <button
        className="bottone-secondario mt-2"
        onClick={() =>
          setRighe((v) => [...v, { id: undefined, dataScadenza: '', importo: '', abbinata: false }])
        }
      >
        Aggiungi una rata
      </button>

      {/* Le rate devono sommare al documento: senza questo vincolo il
          previsionale peserebbe un importo diverso da quello che la fattura
          vale davvero. */}
      <div className="mt-4 rounded-md bg-inchiostro-50 p-3 text-sm">
        Le rate sommano <span className="num font-medium">{formattaEuro(somma)}</span> su{' '}
        <span className="num font-medium">{formattaEuro(documento.totaleCent)}</span> del documento.{' '}
        {scarto === 0 ? (
          <span className="font-medium text-incassi">Tornano.</span>
        ) : (
          <span className="text-allerta">
            {scarto > 0 ? 'Eccedono' : 'Mancano'} <span className="num">{formattaEuro(Math.abs(scarto))}</span>.
          </span>
        )}
      </div>

      {/* La nota è obbligatoria, e non per burocrazia: correggere una scadenza
          significa scostarsi da quello che la fattura dichiara. Fra sei mesi la
          domanda non sarà «questa data è giusta?» ma «chi l'ha cambiata, e
          perché» — e senza la ragione una correzione legittima e un difetto del
          programma si assomigliano. */}
      <div className="mt-4">
        <Campo
          etichetta="Perché la stai correggendo"
          aiuto="Resta a registro con il tuo nome e la data. Non si può cancellare."
        >
          <input
            className="campo"
            value={nota}
            onChange={(e) => setNota(e.target.value)}
            placeholder="Es. refuso nell’XML del fornitore: 2016 invece di 2026"
          />
        </Campo>
      </div>

      <div className="mt-4 flex items-center justify-end gap-2">
        {nota.trim().length < 3 && (
          <span className="mr-auto text-xs text-inchiostro-500">Scrivi la ragione per poter salvare.</span>
        )}
        <button className="bottone-secondario" onClick={onChiudi}>
          Annulla
        </button>
        <button className="bottone-primario" onClick={salva} disabled={!puoSalvare || attesa}>
          {attesa ? 'Salvataggio…' : 'Salva le scadenze'}
        </button>
      </div>
    </Dialogo>
  );
}

/** Riga di una scheda dati: etichetta a sinistra, valore a destra. */
/**
 * La voce del previsionale di una fattura, modificabile sul posto.
 *
 * Prima si sceglieva solo creando una fattura a mano: quelle arrivate dallo SdI
 * o da Fatture in Cloud restavano con la voce che avevano, e non c'era modo di
 * correggerla — la 961 FMI di EXAD stava su «Acquisti» mentre la stima di EXAD
 * stava su «Fornitore Materiale», e in tabella finivano su due righe diverse.
 * Cambia solo la voce: righe e importi di un documento ricevuto non si toccano.
 */
function CampoVoce({ documento, onCambiata }: { documento: Documento; onCambiata: () => void }) {
  const avvisa = useAvvisi();
  const categorie = useCarico(() => api.get<Categoria[]>('/api/categorie'), []);
  const { piatto } = useAlberoCategorie(categorie.dati ?? []);
  const [salvo, setSalvo] = useState(false);
  const verso = documento.tipo === 'ATTIVA' ? 'ENTRATA' : 'USCITA';
  const scelte = piatto.filter((p) => p.categoria.verso === verso && !p.categoria.sistema);

  const [conferma, setConferma] = useState<{ valore: string; movimenti: MovimentiDellaVoce } | null>(null);

  const salva = async (valore: string, movimenti: string[]) => {
    setSalvo(true);
    try {
      await api.put(`/api/documenti/${documento.id}/categoria`, { categoriaId: valore || null, movimenti });
      avvisa(
        movimenti.length
          ? `Voce aggiornata sulla fattura e su ${movimenti.length === 1 ? '1 movimento' : `${movimenti.length} movimenti`}.`
          : 'Voce del previsionale aggiornata.',
      );
      setConferma(null);
      onCambiata();
    } catch (e) {
      avvisa(e instanceof Error ? e.message : 'Modifica non riuscita.', 'errore');
    } finally {
      setSalvo(false);
    }
  };

  // Prima di cambiare, si guarda se ci sono pagamenti abbinati rimasti sulla
  // voce vecchia: se ci sono, si chiede se spostarli; se no si salva e basta.
  const cambia = async (valore: string) => {
    setSalvo(true);
    try {
      const movimenti = await api.get<MovimentiDellaVoce>(`/api/documenti/${documento.id}/movimenti-della-voce`);
      if (movimenti.daSpostare.length === 0 && movimenti.conAltreFatture.length === 0) {
        await salva(valore, []);
        return;
      }
      setConferma({ valore, movimenti });
    } catch (e) {
      avvisa(e instanceof Error ? e.message : 'Modifica non riuscita.', 'errore');
    } finally {
      setSalvo(false);
    }
  };

  if (categorie.caricamento) return <span className="text-inchiostro-400">…</span>;
  const nomeDi = (id: string) =>
    id ? (scelte.find((p) => p.categoria.id === id)?.categoria.nome ?? '—') : 'come l’anagrafica';
  return (
    <>
      {conferma && (
        <DialogoMovimentiDellaVoce
          documento={documento}
          nuovaVoce={nomeDi(conferma.valore)}
          movimenti={conferma.movimenti}
          salvo={salvo}
          onChiudi={() => setConferma(null)}
          onConferma={(ids) => void salva(conferma.valore, ids)}
        />
      )}
      <select
        className="campo py-1 text-sm"
        value={documento.categoriaId ?? ''}
        disabled={salvo}
        onChange={(e) => void cambia(e.target.value)}
      >
        <option value="">Come l’anagrafica</option>
        {scelte.map(({ categoria, livello }) => (
          <option key={categoria.id} value={categoria.id}>
            {' '.repeat(livello * 2)}
            {categoria.nome}
          </option>
        ))}
      </select>
    </>
  );
}

interface MovimentoDellaVoce {
  id: string;
  data: string;
  dicitura: string;
  importoCent: number;
}

interface MovimentiDellaVoce {
  daSpostare: MovimentoDellaVoce[];
  conAltreFatture: MovimentoDellaVoce[];
  conVoceScelta: number;
  seguonoGia: number;
}

/**
 * Quando si cambia la voce di una fattura già pagata: i pagamenti abbinati
 * hanno quasi sempre la voce copiata all'abbinamento, e la voce del movimento
 * vince su quella della fattura. Senza chiedere, il pagamento resterebbe sulla
 * voce vecchia e la fattura andrebbe sulla nuova. Qui si sceglie quali spostare.
 */
function DialogoMovimentiDellaVoce({
  documento,
  nuovaVoce,
  movimenti,
  salvo,
  onChiudi,
  onConferma,
}: {
  documento: Documento;
  nuovaVoce: string;
  movimenti: MovimentiDellaVoce;
  salvo: boolean;
  onChiudi: () => void;
  onConferma: (ids: string[]) => void;
}) {
  const [scelti, setScelti] = useState<Set<string>>(() => new Set(movimenti.daSpostare.map((m) => m.id)));
  const inverti = (id: string) =>
    setScelti((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const riga = (m: MovimentoDellaVoce, casella: ReactNode) => (
    <tr key={m.id} className="border-t border-inchiostro-100">
      <td className="w-8 py-1.5">{casella}</td>
      <td className="num py-1.5 pr-3 whitespace-nowrap">{formattaData(m.data)}</td>
      <td className="py-1.5 pr-3">{m.dicitura}</td>
      <td className="num py-1.5 text-right whitespace-nowrap">{formattaEuro(m.importoCent)}</td>
    </tr>
  );

  return (
    <Dialogo aperto titolo={`Voce di ${documento.numero}: e i pagamenti?`} onChiudi={onChiudi} larghezza="max-w-3xl">
      {/* Il dialogo nasce dentro la riga della scheda, che è allineata a destra
          e in grassetto: qui si torna al testo normale. */}
      <div className="text-left font-normal">
        <p className="text-sm text-inchiostro-700">
          La fattura passa su <span className="font-medium">«{nuovaVoce}»</span>. Questi pagamenti abbinati hanno ancora
          la voce vecchia della fattura, copiata quando sono stati abbinati: la voce del movimento vince su quella della
          fattura, quindi se non li sposti restano dove sono.
        </p>

        {movimenti.daSpostare.length > 0 && (
          <table className="mt-3 w-full text-sm">
            <tbody>
              {movimenti.daSpostare.map((m) =>
                riga(
                  m,
                  <input
                    type="checkbox"
                    checked={scelti.has(m.id)}
                    onChange={() => inverti(m.id)}
                    aria-label={`Sposta ${m.dicitura}`}
                  />,
                ),
              )}
            </tbody>
          </table>
        )}

        {movimenti.conAltreFatture.length > 0 && (
          <>
            <p className="mt-4 text-sm text-inchiostro-600">
              Questi pagano anche altre fatture: una voce sola sul bonifico intero sposterebbe anche quelle, quindi non li
              tocco da qui. Si cambiano dalla pagina del movimento.
            </p>
            <table className="mt-2 w-full text-sm text-inchiostro-500">
              <tbody>{movimenti.conAltreFatture.map((m) => riga(m, null))}</tbody>
            </table>
          </>
        )}

        {(movimenti.conVoceScelta > 0 || movimenti.seguonoGia > 0) && (
          <p className="mt-3 text-xs text-inchiostro-500">
            {movimenti.conVoceScelta > 0 &&
              `${movimenti.conVoceScelta === 1 ? '1 pagamento ha' : `${movimenti.conVoceScelta} pagamenti hanno`} una voce diversa scelta a mano: resta com’è. `}
            {movimenti.seguonoGia > 0 &&
              `${movimenti.seguonoGia === 1 ? '1 pagamento non ha' : `${movimenti.seguonoGia} pagamenti non hanno`} una voce propria: segue già la fattura.`}
          </p>
        )}

        <div className="mt-4 flex items-center justify-end gap-2">
          <button className="bottone-secondario" onClick={onChiudi} disabled={salvo}>
            Annulla
          </button>
          <button className="bottone-secondario" onClick={() => onConferma([])} disabled={salvo}>
            Solo la fattura
          </button>
          {movimenti.daSpostare.length > 0 && (
            <button className="bottone-primario" onClick={() => onConferma([...scelti])} disabled={salvo || scelti.size === 0}>
              {salvo
                ? 'Salvo…'
                : `Fattura e ${scelti.size === 1 ? '1 pagamento' : `${scelti.size} pagamenti`}`}
            </button>
          )}
        </div>
      </div>
    </Dialogo>
  );
}

function Voce({ t, v, aiuto }: { t: string; v: ReactNode; aiuto?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-inchiostro-600" title={aiuto}>
        {t}
        {aiuto && <span className="ml-1 text-inchiostro-400">ⓘ</span>}
      </dt>
      <dd className="text-right font-medium">{v}</dd>
    </div>
  );
}

interface Candidato extends Movimento {
  conto: { id: string; nome: string; tipo: string };
}

/**
 * Cerca il movimento partendo **dalla fattura**. Senza ricerca la lista è
 * ordinata per somiglianza (importo vicino al residuo, data vicina a quella
 * attesa); appena si scrive qualcosa passa all'ordine per data, perché da quel
 * momento è chi cerca a sapere cosa sta cercando.
 */
function DialogoCercaMovimento({
  documento,
  onChiudi,
  onAbbinato,
}: {
  documento: Documento;
  onChiudi: () => void;
  onAbbinato: () => void;
}) {
  const { organizzazione } = useSessione();
  const avvisa = useAvvisi();
  const [cerca, setCerca] = useState('');
  const [da, setDa] = useState('');
  const [a, setA] = useState('');
  const [inCorso, setInCorso] = useState(false);
  const cercaRitardata = useDebounce(cerca);

  const query = new URLSearchParams();
  if (cercaRitardata) query.set('cerca', cercaRitardata);
  if (da) query.set('da', da);
  if (a) query.set('a', a);

  const risultato = useCarico(
    () =>
      api.get<{ candidati: Candidato[]; totale: number }>(
        `/api/riconciliazione/movimenti-candidati/${documento.id}?${query}`,
      ),
    [documento.id, cercaRitardata, da, a],
  );

  const abbina = async (movimentoId: string) => {
    setInCorso(true);
    try {
      await api.post('/api/riconciliazione/abbina', { movimentoId, documentoId: documento.id });
      avvisa('Movimento abbinato alla fattura.');
      onAbbinato();
    } catch (e) {
      avvisa(e instanceof Error ? e.message : 'Abbinamento non riuscito.', 'errore');
    } finally {
      setInCorso(false);
    }
  };

  const candidati = risultato.dati?.candidati ?? [];

  return (
    <Dialogo aperto titolo={`Cerca il movimento di ${documento.numero}`} onChiudi={onChiudi} larghezza="max-w-4xl">
      <p className="mb-3 text-sm text-inchiostro-500">
        Da abbinare <span className="num font-medium">{formattaEuro(documento.residuoCent)}</span>, cassa attesa il{' '}
        <span className="num">{formattaData(documento.dataCassaAttesa, organizzazione.formatoData)}</span>. Vengono
        mostrati solo movimenti del verso giusto e non ancora abbinati del tutto.
      </p>

      <div className="mb-3 flex flex-wrap items-end gap-3">
        <Campo etichetta="Cerca nella causale" larghezza="w-72">
          <input
            className="campo"
            value={cerca}
            onChange={(e) => setCerca(e.target.value)}
            placeholder="Es. MEDIATEL"
            autoFocus
          />
        </Campo>
        <Campo etichetta="Dal" larghezza="w-40">
          <input type="date" className="campo" value={da} onChange={(e) => setDa(e.target.value)} />
        </Campo>
        <Campo etichetta="Al" larghezza="w-40">
          <input type="date" className="campo" value={a} onChange={(e) => setA(e.target.value)} />
        </Campo>
        {(cerca || da || a) && (
          <button
            className="bottone-secondario"
            onClick={() => {
              setCerca('');
              setDa('');
              setA('');
            }}
          >
            Azzera
          </button>
        )}
      </div>

      {risultato.caricamento ? (
        <Caricamento />
      ) : risultato.errore ? (
        <Errore testo={risultato.errore} riprova={risultato.ricarica} />
      ) : candidati.length === 0 ? (
        <Vuoto
          titolo="Nessun movimento trovato"
          testo="Prova con meno lettere della causale, o allarga il periodo."
        />
      ) : (
        <>
          <p className="mb-2 text-xs text-inchiostro-500">
            {risultato.dati!.totale > candidati.length
              ? `Mostrati i primi ${candidati.length} di ${risultato.dati!.totale}: restringi la ricerca per vedere gli altri.`
              : `${candidati.length} movimenti.`}
          </p>
          <div className="max-h-96 overflow-y-auto">
            <table className="dati">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Conto</th>
                  <th>Causale</th>
                  <th className="text-right">Importo</th>
                  <th className="text-right">Ancora libero</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {candidati.map((c) => (
                  <tr key={c.id}>
                    <td className="num whitespace-nowrap">{formattaData(c.data, organizzazione.formatoData)}</td>
                    <td className="whitespace-nowrap">{c.conto.nome}</td>
                    <td className="max-w-96 truncate" title={c.dicitura}>
                      {c.dicitura}
                    </td>
                    <td className="text-right">
                      <Importo cent={c.importoCent} tono="auto" />
                    </td>
                    <td className="text-right">
                      <Importo cent={c.residuoCent} />
                    </td>
                    <td className="text-right">
                      <button
                        className="bottone-primario px-2 py-1 text-xs"
                        disabled={inCorso}
                        onClick={() => abbina(c.id)}
                      >
                        Abbina
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Dialogo>
  );
}

/**
 * Compensare una fattura con una di verso opposto della stessa controparte.
 *
 * Il caso è lo scambio merce: si emette fattura, la controparte ne emette una
 * in compensazione, e nessuno dei due paga. Qui non si propone niente — le
 * fatture le sceglie chi sa dell'accordo. Con lo stesso cliente alcune
 * rientrano nello scambio e altre sono dovute in denaro, e due importi che
 * tornano non vogliono dire che le parti li abbiano compensati.
 */
function DialogoCompensa({
  documento,
  onChiudi,
  onFatto,
}: {
  documento: Documento;
  onChiudi: () => void;
  onFatto: () => void;
}) {
  const { organizzazione } = useSessione();
  const avvisa = useAvvisi();
  const [scelto, setScelto] = useState<DocumentoCompensabile | null>(null);
  const [importo, setImporto] = useState('');
  const [data, setData] = useState(oggi());
  const [nota, setNota] = useState('');
  const [attesa, setAttesa] = useState(false);
  const { dati, caricamento, errore } = useCarico(
    () => api.get<{ candidati: DocumentoCompensabile[]; motivo?: string }>(`/api/documenti/${documento.id}/compensabili`),
    [documento.id],
  );

  const candidati = dati?.candidati ?? [];
  const massimo = scelto ? compensabileCent(documento.residuoCent, scelto.residuoCent) : 0;
  const importoCent = inCent(importo);
  // La stessa regola che il server applica prima di scrivere: se qui dicesse
  // un'altra cosa, a schermo risulterebbe accettabile un importo che poi viene
  // rifiutato.
  const motivo = scelto ? motivoNonCompensabile(documento.residuoCent, scelto.residuoCent, importoCent) : null;
  const puoSalvare = Boolean(scelto) && !motivo && data.length === 10 && nota.trim().length >= 3;

  const scegli = (c: DocumentoCompensabile) => {
    setScelto(c);
    // Il massimo capiente è un punto di partenza, non una proposta: è la cifra
    // che chiude il più piccolo dei due, ed è quasi sempre quella giusta.
    setImporto(inEuro(compensabileCent(documento.residuoCent, c.residuoCent)));
  };

  const salva = async () => {
    if (!scelto) return;
    setAttesa(true);
    try {
      // Chi sta da che parte lo decide il server dal verso dei due documenti:
      // con le note di credito il tipo non basta, e due regole che devono
      // coincidere prima o poi divergono.
      await api.post('/api/compensazioni', {
        documentiId: [documento.id, scelto.id],
        importoCent,
        data,
        nota: nota.trim(),
      });
      avvisa(`Compensate ${formattaEuro(importoCent)} con la fattura ${scelto.numero}. In banca non passa niente.`);
      onFatto();
    } catch (e) {
      avvisa(e instanceof Error ? e.message : 'Compensazione non riuscita.', 'errore');
    } finally {
      setAttesa(false);
    }
  };

  const dopoQuesto = documento.residuoCent - (motivo ? 0 : importoCent);
  const dopoAltro = (scelto?.residuoCent ?? 0) - (motivo ? 0 : importoCent);

  return (
    <Dialogo aperto titolo="Compensa con un altro documento" onChiudi={onChiudi} larghezza="max-w-3xl">
      <div className="mb-3 rounded-md bg-inchiostro-50 p-3 text-sm">
        <p className="font-medium">
          {documento.numero} · {documento.controparte?.denominazione ?? 'senza controparte'}
        </p>
        <p className="mt-0.5 text-inchiostro-600">
          {/* Una nota di credito attiva è un documento `ATTIVA` che però
              dobbiamo noi: scrivere «da incassare» sarebbe il contrario. */}
          {versoCompensazione(documento.tipo, documento.notaDiCredito) === 'CREDITO'
            ? 'Da incassare'
            : 'Da pagare'}{' '}
          <span className="num font-medium">{formattaEuro(documento.residuoCent)}</span> su{' '}
          {formattaEuro(documento.totaleCent)}
        </p>
      </div>

      {caricamento && <Caricamento />}
      {errore && <Errore testo={errore} />}
      {!caricamento && candidati.length === 0 && (
        <Vuoto
          titolo="Niente da compensare"
          testo={
            dati?.motivo ??
            versoCompensazione(documento.tipo, documento.notaDiCredito) === 'CREDITO'
              ? 'Non ci sono debiti aperti verso la stessa controparte: né fatture passive né note di credito emesse.'
              : 'Non ci sono crediti aperti verso la stessa controparte: né fatture attive né note di credito ricevute.'
          }
        />
      )}

      {candidati.length > 0 && (
        <>
          <p className="mb-2 text-xs text-inchiostro-500">
            {versoCompensazione(documento.tipo, documento.notaDiCredito) === 'CREDITO'
              ? 'Solo debiti aperti verso la stessa controparte — fatture passive e note di credito che le abbiamo emesso.'
              : 'Solo crediti aperti verso la stessa controparte — fatture attive e note di credito che ci ha emesso.'}{' '}
            Stessa partita IVA, o stesso codice fiscale per chi la P.IVA non ce l’ha. Scegli tu quali rientrano
            nell’accordo: Mastro non ne propone nessuna.
          </p>
          <div className="max-h-72 overflow-y-auto rounded-md border border-inchiostro-200">
            <table className="dati">
              <thead>
                <tr>
                  <th className="w-10" />
                  <th>Documento</th>
                  <th className="w-32">Competenza</th>
                  <th className="w-32 text-right">Totale</th>
                  <th className="w-32 text-right">Residuo</th>
                </tr>
              </thead>
              <tbody>
                {candidati.map((c) => (
                  <tr
                    key={c.id}
                    className={scelto?.id === c.id ? 'bg-inchiostro-50' : undefined}
                    onClick={() => scegli(c)}
                  >
                    <td>
                      <input
                        type="radio"
                        name="compensabile"
                        aria-label={`Compensa con la fattura ${c.numero}`}
                        checked={scelto?.id === c.id}
                        onChange={() => scegli(c)}
                      />
                    </td>
                    <td>
                      {c.numero}
                      {c.notaDiCredito && (
                        <span className="ml-1.5">
                          <Etichetta
                            testo="nota di credito"
                            tono="attenzione"
                            titolo="Il flusso va nel verso opposto al suo tipo: una emessa è un nostro debito, una ricevuta un credito."
                          />
                        </span>
                      )}
                    </td>
                    <td className="num">{formattaData(c.dataCompetenza, organizzazione.formatoData)}</td>
                    <td className="text-right"><Importo cent={c.totaleCent} /></td>
                    <td className="text-right"><Importo cent={c.residuoCent} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {scelto && (
        <>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <Campo
              etichetta="Quanto si compensa"
              aiuto={`Al massimo ${formattaEuro(massimo)}: oltre, una delle due andrebbe sotto zero.`}
            >
              <input
                className="campo num text-right"
                aria-label="Importo da compensare"
                value={importo}
                onChange={(e) => setImporto(e.target.value)}
                placeholder="0,00"
              />
            </Campo>
            <Campo etichetta="Data dell’accordo" aiuto="Non quella di oggi: è la data che finisce nella lettera.">
              <input type="date" className="campo" value={data} onChange={(e) => setData(e.target.value)} />
            </Campo>
          </div>

          {/* Il conteggio è il punto: una compensazione si valuta da cosa resta
              aperto dopo, non da quanto vale. */}
          <div className="mt-3 rounded-md bg-inchiostro-50 p-3 text-sm">
            {motivo ? (
              <span className="text-allerta">{motivo}</span>
            ) : (
              <>
                <div className="flex justify-between">
                  <span>Dopo, sulla {documento.numero} resta</span>
                  <span className="num font-medium">{formattaEuro(dopoQuesto)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Dopo, sulla {scelto.numero} resta</span>
                  <span className="num font-medium">{formattaEuro(dopoAltro)}</span>
                </div>
                <p className="mt-2 text-xs text-inchiostro-500">
                  In banca non passa niente: la cassa prevista non cambia, calano i due residui.
                </p>
              </>
            )}
          </div>

          <div className="mt-4">
            <Campo
              etichetta="In base a cosa"
              aiuto="Resta a registro su tutte e due le fatture, con il tuo nome e la data."
            >
              <input
                className="campo"
                value={nota}
                onChange={(e) => setNota(e.target.value)}
                placeholder="Es. contratto di scambio merce del 12/03/2026"
              />
            </Campo>
          </div>
        </>
      )}

      <div className="mt-4 flex items-center justify-end gap-2">
        {scelto && nota.trim().length < 3 && (
          <span className="mr-auto text-xs text-inchiostro-500">Scrivi in base a cosa, per poter salvare.</span>
        )}
        <button className="bottone-secondario" onClick={onChiudi}>
          Annulla
        </button>
        <button className="bottone-primario" onClick={salva} disabled={!puoSalvare || attesa}>
          {attesa ? 'Registrazione…' : 'Registra la compensazione'}
        </button>
      </div>
    </Dialogo>
  );
}
